import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Field, TextInput, TextArea, SelectInput, EmptyState } from "@/components/Fields";
import { getEmpresa, updateEmpresa, getEnums, listPlanes, createPlan } from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, FileText, Plus, Pencil } from "lucide-react";

const btnPrimary = "inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800 transition-colors";

const ESTADO_LABEL = {
  borrador: { txt: "Borrador", cls: "bg-slate-100 text-slate-600" },
  en_revision: { txt: "En revisión", cls: "bg-amber-100 text-amber-800" },
  generado: { txt: "Generado", cls: "bg-blue-100 text-blue-800" },
};

function DataRow({ label, value }) {
  return (
    <div className="py-3 border-b border-slate-100 last:border-0">
      <div className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400">{label}</div>
      <div className="text-slate-900 mt-0.5">{value || "—"}</div>
    </div>
  );
}

export default function EmpresaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [empresa, setEmpresa] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const load = useCallback(() => {
    getEmpresa(id).then(setEmpresa).catch(() => navigate("/empresas"));
    listPlanes({ empresa_id: id }).then(setPlanes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  useEffect(() => { load(); getEnums().then((e) => setModalidades(e.modalidades_preventivas || [])); }, [load]);

  const openEdit = () => { setForm({ ...empresa }); setOpen(true); };
  const save = async () => {
    if (!form.razon_social?.trim()) return toast.error("La razón social es obligatoria");
    try {
      await updateEmpresa(id, { ...form, num_trabajadores: Number(form.num_trabajadores) || 0 });
      toast.success("Empresa actualizada"); setOpen(false); load();
    } catch { toast.error("No se pudo guardar"); }
  };

  const nuevoPlan = async () => {
    try {
      const p = await createPlan({ empresa_id: id });
      navigate(`/planes/${p.id}`);
    } catch { toast.error("No se pudo crear el plan"); }
  };

  if (!empresa) return <Layout title="Cargando…"><div className="text-slate-500">Cargando empresa…</div></Layout>;

  return (
    <Layout
      title={empresa.razon_social}
      subtitle={empresa.actividad || "Ficha de empresa"}
      actions={
        <button onClick={() => navigate("/empresas")} data-testid="back-empresas" className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">
          <ArrowLeft className="w-5 h-5" /> <span className="hidden sm:inline">Empresas</span>
        </button>
      }
    >
      <div className="space-y-8 max-w-3xl">
        <div className="bg-white border border-slate-200 rounded-xl p-6 lg:p-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg text-slate-800">Datos de la empresa</h3>
            <button onClick={openEdit} data-testid="empresa-editar" className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"><Pencil className="w-4 h-4" /> Editar</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <DataRow label="Razón social" value={empresa.razon_social} />
            <DataRow label="NIF" value={empresa.nif} />
            <DataRow label="Actividad" value={empresa.actividad} />
            <DataRow label="Modalidad preventiva" value={empresa.modalidad_preventiva} />
            <DataRow label="Dirección" value={empresa.direccion} />
            <DataRow label="Nº trabajadores" value={empresa.num_trabajadores} />
            <DataRow label="Teléfono" value={empresa.telefono} />
            <DataRow label="Correo electrónico" value={empresa.email} />
          </div>
          {empresa.observaciones && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-xs uppercase tracking-[0.12em] font-semibold text-slate-400 mb-1">Observaciones</div>
              <p className="text-slate-700 whitespace-pre-wrap">{empresa.observaciones}</p>
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-lg text-slate-800">Planes de PRL ({planes.length})</h3>
            <button onClick={nuevoPlan} data-testid="empresa-nuevo-plan" className={btnPrimary}><Plus className="w-5 h-5" /> Nuevo plan</button>
          </div>
          {planes.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl">
              <EmptyState icon={FileText} title="Sin planes de PRL" description="Crea el primer Plan de PRL de esta empresa." />
            </div>
          ) : (
            <div className="space-y-3">
              {planes.map((p) => {
                const est = ESTADO_LABEL[p.estado] || ESTADO_LABEL.borrador;
                return (
                  <button
                    key={p.id}
                    data-testid={`empresa-plan-${p.id}`}
                    onClick={() => navigate(`/planes/${p.id}`)}
                    className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 hover:border-blue-300 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><FileText className="w-5 h-5 text-blue-700" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-semibold text-slate-900">{(p.centro && p.centro.nombre) || "Plan de PRL"}</div>
                      <div className="text-sm text-slate-500">Actualizado {formatFecha(p.updated_at)}</div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${est.cls}`}>{est.txt}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-xl">Editar empresa</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 py-2">
            <div className="sm:col-span-2"><Field label="Razón social" required><TextInput testId="fe-razon" value={form.razon_social || ""} onChange={set("razon_social")} /></Field></div>
            <Field label="NIF"><TextInput testId="fe-nif" value={form.nif || ""} onChange={set("nif")} /></Field>
            <Field label="Actividad"><TextInput testId="fe-actividad" value={form.actividad || ""} onChange={set("actividad")} /></Field>
            <div className="sm:col-span-2"><Field label="Dirección"><TextInput testId="fe-direccion" value={form.direccion || ""} onChange={set("direccion")} /></Field></div>
            <Field label="Nº trabajadores"><TextInput testId="fe-num" type="number" min="0" value={form.num_trabajadores || 0} onChange={set("num_trabajadores")} /></Field>
            <Field label="Modalidad preventiva"><SelectInput testId="fe-modalidad" value={form.modalidad_preventiva || ""} onChange={set("modalidad_preventiva")} options={modalidades} /></Field>
            <Field label="Teléfono"><TextInput testId="fe-tel" value={form.telefono || ""} onChange={set("telefono")} /></Field>
            <Field label="Correo electrónico"><TextInput testId="fe-email" value={form.email || ""} onChange={set("email")} /></Field>
            <div className="sm:col-span-2"><Field label="Observaciones"><TextArea testId="fe-obs" value={form.observaciones || ""} onChange={set("observaciones")} /></Field></div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="h-12 px-5 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
            <button onClick={save} data-testid="empresa-save" className="h-12 px-6 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">Guardar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
